// client/src/components/offlinePOIMarker.js
import React, { useRef, useMemo } from 'react';
import L from 'leaflet';
import { Marker, Tooltip, useMap } from 'react-leaflet';
import OfflineEditPOI from './OfflineEditPOI';

function getColouredIconHTML(iconUrl, colour, size = 64, labelBorder = 1, labelBgColour = '#000', labelOpacity = 1) {
  const showBorder = Number(labelBorder) === 1;
  return `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      pointer-events: none;
      opacity: ${labelOpacity};
    ">
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        ${showBorder ? `border: 3px solid ${colour};` : 'border: none;'}
        font-weight: bold;
      ">
        <img src="${iconUrl}" style="width: ${size * 0.75}px; height: ${size * 0.75}px; object-fit: contain; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.22)); opacity: ${labelOpacity};" />
      </div>
    </div>
  `;
}

function getLabelHTML(label, shape, colour = '#fff', size = 64, labelBorder = 1, labelBgColour = '#000', labelOpacity = 1) {
  const showBorder = Number(labelBorder) === 1;
  return `
    <div style="
      background: ${labelBgColour};
      color: #fff;
      border-radius: 10px;
      ${showBorder ? `border: 3px solid ${colour};` : 'border: none;'}
      font-weight: bold;
      padding: 6px 18px;
      font-size: 21px;
      display: inline-block;
      text-align: center;
      min-width: 60px;
      box-shadow: 0 2px 10px #0006;
      position: relative;
      opacity: ${labelOpacity};
    ">
      ${label}
      <div style="
        position: absolute;
        left: 50%;
        bottom: -15px;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 12px solid transparent;
        border-right: 12px solid transparent;
        border-top: 13px solid ${colour};
        ${showBorder ? '' : 'display: none;'}
      "></div>
    </div>
  `;
}

function OfflinePOIMarker({
  poi,
  customCategories = [],
  customTypes = [],
  shouldFlash = false,
  typeColours = {},
  showLabel = false,
  found = false,
  onToggleFound = () => {},
  opacity = 1,
  labelOpacity = 1,
  iconOpacity = 1,
  onMouseEnter,
  onMouseLeave,
  showPoiNames = false,
  mapId,
  userId,
  mapOwnerId,
  onCategoryUpdate,
  onPOIUpdate,
  onTypeUpdate,
  modalOpen,
  isEditing,
  onOpenModal,
  onCloseModal,
  onStartEdit,
  onStopEdit,
  ...rest
}) {
  const markerRef = useRef();
  const map = useMap();

  const categoryObj = customCategories.find(
    c => c.id === poi.category_id || c.id === poi.category
  ) || {};
  const typeObj = customTypes.find(
    t => t.id === poi.type_id || t.id === poi.type
  ) || {};

  function getShapeHTML() {
    const size = 10;
    const color = poi.colour || typeObj.color || typeColours[poi.type] || '#ff0000';
    const opacityStyle = `opacity: ${iconOpacity};`;
    switch (poi.shape || typeObj.shape || 'circle') {
      case 'square':
        return `<div style="width:${size}px;height:${size}px;background:${color};${opacityStyle}"></div>`;
      case 'triangle':
        return `<div style="width:0;height:0;border-left:${size/2}px solid transparent;border-right:${size/2}px solid transparent;border-bottom:${size}px solid ${color};${opacityStyle}"></div>`;
      case 'star':
        return `<div style="font-size:${size+3}px; color:${color};${opacityStyle}">★</div>`;
      case 'circle':
      default:
        return `<div style="width:${size}px;height:${size}px;background:${color};border-radius:50%;${opacityStyle}"></div>`;
    }
  }

  const icon = useMemo(() => L.divIcon({
    className: 'poi-marker',
    html: `<div class="${shouldFlash ? 'flash' : ''}">${getShapeHTML()}</div>`,
    iconAnchor: [5, 5]
  }), [shouldFlash, poi.type, poi.shape, typeObj.shape, typeObj.color, typeColours, poi.category, iconOpacity, poi.colour]);

  const handleMarkerClick = () => {
    if (modalOpen) return;
    if (typeof onOpenModal === 'function') onOpenModal(poi.id);
  };

  const handleCloseModal = () => {
    if (typeof onStopEdit === 'function') onStopEdit();
    if (typeof onCloseModal === 'function') onCloseModal();
  };

  const handleEditClick = () => {
    if (typeof onStartEdit === 'function') onStartEdit();
  };

  const panelWidth = 360;

  const modalBackdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 2000,
    cursor: 'default',
  };

  const centerModalStyle = {
    position: "fixed",
    left: "50%",
    top: "10%",
    transform: "translate(-50%, 0)",
    width: panelWidth,
    background: "#232427",
    borderRadius: 16,
    boxShadow: '0 6px 24px #000b',
    padding: 12,
    color: "#fff",
    minHeight: 180,
    border: "1.5px solid #23252a",
    zIndex: 2100,
  };

  const centerModalEditStyle = {
    ...centerModalStyle,
    zIndex: 2101,
    overflow: 'auto',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
  };

  const labelBorder = typeof poi.label_border === 'undefined' || poi.label_border === null ? 1 : Number(poi.label_border);
  const labelBgColour = poi.label_bg_colour || '#000';

  const labelHtml = showPoiNames && poi.poi_name && poi.poi_name.trim().length > 0
    ? getLabelHTML(
        poi.poi_name,
        poi.shape || typeObj.shape || 'circle',
        poi.colour || typeObj.color || typeColours[poi.type] || '#fff',
        64,
        labelBorder,
        labelBgColour,
        labelOpacity
      )
    : (poi.icon_file && typeof poi.icon_file === 'string' && poi.icon_file.length > 0
      ? getColouredIconHTML(
          poi.icon_file,
          poi.colour || typeObj.color || typeColours[poi.type] || '#fff',
          64,
          labelBorder,
          '#000',
          labelOpacity
        )
      : getLabelHTML(
          typeObj.name || (poi.type ? `${poi.type}` : '<i>(No name)</i>'),
          poi.shape || typeObj.shape || 'circle',
          poi.colour || typeObj.color || typeColours[poi.type] || '#fff',
          64,
          labelBorder,
          labelBgColour,
          labelOpacity
        )
    );

  return (
    <>
      <Marker
        key={poi.id + '_' + (shouldFlash ? 'flash' : 'noflash')}
        position={[poi.y, poi.x]}
        icon={icon}
        ref={markerRef}
        eventHandlers={{
          click: handleMarkerClick,
          mouseover: () => onMouseEnter && onMouseEnter(poi.id),
          mouseout: () => onMouseLeave && onMouseLeave(null),
        }}
        opacity={opacity}
        zIndexOffset={found ? -500 : 0}
        {...rest}
      >
        {showLabel && (
          <Tooltip
            direction="top"
            offset={[0, -10]}
            opacity={labelOpacity}
            permanent
            interactive={false}
            className="poi-label-tooltip"
          >
            <span
              key={`${poi.id}-${labelOpacity}`}
              style={{ opacity: labelOpacity }}
              dangerouslySetInnerHTML={{ __html: labelHtml }}
            />
          </Tooltip>
        )}
      </Marker>

      {modalOpen && !isEditing && (
        <>
          <div
            style={modalBackdropStyle}
            onClick={handleCloseModal}
          />
          <div style={centerModalStyle} onClick={e => e.stopPropagation()}>
            <button
              onClick={handleCloseModal}
              style={{
                position: "absolute",
                top: 8,
                right: 10,
                background: "#444",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                padding: "4px 9px",
                cursor: "pointer"
              }}
            >
              ✕
            </button>

            <div style={{ marginBottom: 5 }}>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 7 }}>
                Name: <span style={{ fontStyle: 'italic', fontWeight: 400 }}>{poi.poi_name || poi.name || "(Unnamed)"}</span>
              </div>
              <div><b>Category:</b> {categoryObj.name || ""}</div>
              <div><b>Type Name:</b> {typeObj.name || ""}</div>
              <div>
                <b>Type Colour:</b>{" "}
                <span style={{
                  background: typeObj.color || poi.colour || "#c00",
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  display: "inline-block",
                  verticalAlign: "middle",
                  border: "2px solid #444",
                  marginLeft: 4
                }} />
              </div>
              <div><b>Type Shape:</b> {typeObj.shape || poi.shape || ""}</div>
              <div><b>Label Border:</b> {labelBorder === 1 ? "Yes" : "No"}</div>
              <div>
                <b>Label Background Colour:</b>{" "}
                <span style={{
                  background: labelBgColour,
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  display: "inline-block",
                  verticalAlign: "middle",
                  border: "2px solid #444",
                  marginLeft: 4
                }} />
              </div>
              <div><b>Description:</b> {poi.description ? poi.description : <span style={{ fontStyle: "italic", color: "#999" }}>(No description)</span>}</div>
              <div>
                <b>Type Icon:</b>{" "}
                {poi.icon_file && typeof poi.icon_file === 'string' && poi.icon_file.length > 0 ? (
                  <img src={poi.icon_file} alt="icon" style={{ width: 22, height: 22, verticalAlign: 'middle', borderRadius: 3, border: '1px solid #222', marginLeft: 6 }} />
                ) : (
                  <span style={{ color: "#888", fontStyle: "italic" }}>None</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
              <button
                onClick={handleEditClick}
                style={{
                  background: "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  padding: "7px 22px",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Edit
              </button>

              <button
                onClick={() => onToggleFound(poi.id)}
                style={{
                  background: found ? "#1ec656" : "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  padding: "7px 22px",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {found ? "Mark POI as Not Found" : "Mark POI as Found"}
              </button>
            </div>

          </div>
        </>
      )}

      {modalOpen && isEditing && (
        <div style={centerModalEditStyle} onClick={e => e.stopPropagation()}>
          <OfflineEditPOI
            poi={poi}
            customCategories={customCategories}
            customTypes={customTypes}
            categoryObj={categoryObj}
            typeObj={typeObj}
            mapId={mapId}
            userId={userId}
            mapOwnerId={mapOwnerId}
            onCategoryUpdate={onCategoryUpdate}
            onTypeUpdate={onTypeUpdate}
            canEdit={true}
            onPOIUpdate={async (updatedPOI, deletedPOIId) => {
              if (typeof onPOIUpdate === 'function') {
                await onPOIUpdate(updatedPOI, deletedPOIId);
              }
            }}
            onClose={() => {
              onStopEdit();
              onCloseModal();
            }}
          />
        </div>
      )}
    </>
  );
}

export default OfflinePOIMarker;
